import React from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';

function getValidationStatusText(tinMatched, lastChecked) {
  if (tinMatched !== null && lastChecked && lastChecked !== null) {
    return `Checked ${ moment(lastChecked).format('MMM DD, YYYY') }`;
  }
  if (tinMatched === null && (lastChecked === null || !lastChecked)) {
    return 'Validation check not made yet';
  }

  const checkedInLast48Hours = moment().diff(lastChecked, 'hours') <= 48;

  if (tinMatched === null && !checkedInLast48Hours) {
    return `Attempted ${ moment(lastChecked).format('MMM DD, YYYY') }`;
  }
  if (tinMatched === null && checkedInLast48Hours) {
    return 'Attempted recently. Can try again in 48hrs';
  }

  return null;
}

function tinCheckSentRecently(tinMatched, lastChecked) {
  const checkedInLast48Hours = lastChecked && moment().diff(lastChecked, 'hours') <= 48;

  if (tinMatched === null && checkedInLast48Hours) {
    return true;
  }
  if (tinMatched === null && !checkedInLast48Hours) {
    return false;
  }

  return null;
}

function getTinCheckDetails(tinMatchResult) {
  const tinCheckDetails = {
    tinMatched: null,
    lastChecked: null
  };

  if (!tinMatchResult || tinMatchResult === null) {
    return tinCheckDetails;
  }

  tinCheckDetails.lastChecked = tinMatchResult.requestTime;

  switch (tinMatchResult.responseCode) {
    case 0: // Match
    case 6: // UnknownTypeMatchOnlyOnSsn
    case 7: // UnknownTypeMatchOnlyOnEin
    case 8: // UnknownTypeMatchSsnAndEin
      tinCheckDetails.tinMatched = true;
      return tinCheckDetails;
    case 2: // TinNotIssued
    case 3: // NotMatched
      tinCheckDetails.tinMatched = false;
      return tinCheckDetails;
    default: // 1: TinMissing; 4: InvalidRequest; 5: DuplicateRequest
      return tinCheckDetails;
  }
}

function CheckNameOrTinChangedOnForm(tinMatchResultById, tinMatchFormProps) {
  if (!tinMatchResultById || !tinMatchFormProps) {
    return false;
  }

  const nameChanged = tinMatchResultById.name?.toLowerCase() !== tinMatchFormProps.name?.toLowerCase();

  const tinChanged = ((tinMatchResultById.tinType === 1 && tinMatchResultById.tin !== tinMatchFormProps.tin?.ssn)
      || (tinMatchResultById.tinType === 2 && tinMatchResultById.tin !== tinMatchFormProps.tin?.ein));

  return (nameChanged || tinChanged);
}

const TinValidationStatusChip = ({ tinMatched, lastChecked, nameOrTinChanged }) => (
  <div>
    { !nameOrTinChanged && tinMatched === true && lastChecked !== null && <div className="chip chip--valid">Valid</div>}
    { !nameOrTinChanged && tinMatched === false && lastChecked !== null && <div className="chip chip--invalid">Invalid</div>}
    { (nameOrTinChanged || (tinMatched === null && lastChecked === null)) && <div className="chip chip--unknown">Unknown</div>}
    { !nameOrTinChanged && tinMatched === null && lastChecked !== null && <div className="chip chip--error">Error</div>}
  </div>
);
TinValidationStatusChip.propTypes = {
  tinMatched: PropTypes.bool,
  lastChecked: PropTypes.string,
  nameOrTinChanged: PropTypes.bool
};
TinValidationStatusChip.defaultProps = {
  tinMatched: null,
  lastChecked: null,
  nameOrTinChanged: false
};

const TinCheckStatus = ({ onCheckTin, formId, tinMatchResult, workflowStage, hasValidation, isIndividualValidation, isImyChildResult,
  tinMatchFormProps, isFormDirty }) => {
  const tinMatchResultById = tinMatchResult?.find(result => result.formId === formId);

  const tinCheckDetails = getTinCheckDetails(tinMatchResultById);

  const nameOrTinChanged = CheckNameOrTinChangedOnForm(tinMatchResultById, tinMatchFormProps);

  if (tinMatchResultById?.checkMade === false || (nameOrTinChanged && tinCheckDetails)) {
    tinCheckDetails.tinMatched = null;
    tinCheckDetails.lastChecked = null;
  }

  const showCheckTinButton = ((workflowStage > 4 || isIndividualValidation)
    && workflowStage < 100) && hasValidation && !isImyChildResult
    && tinCheckDetails && tinCheckDetails.tinMatched === null && !isFormDirty;

  return (<div className="tin-validation-status">
    <label>TIN Validation:</label>
    <div className="status-bar col-12">
      <div className="status-label col-10">
        {tinCheckDetails && <TinValidationStatusChip
          tinMatched={ tinCheckDetails.tinMatched }
          lastChecked={ tinCheckDetails.lastChecked }
          nameOrTinChanged={ nameOrTinChanged }
        />}
        {tinCheckDetails && <div className="last-checked-label">{getValidationStatusText(tinCheckDetails.tinMatched, tinCheckDetails.lastChecked)}</div>}
      </div>
      <div className="button-container col-2">
        {showCheckTinButton && !nameOrTinChanged && tinCheckDetails && tinCheckSentRecently(tinCheckDetails.tinMatched, tinCheckDetails.lastChecked) && <div className="check-tin-sent">Sent</div>}
        {showCheckTinButton && (nameOrTinChanged || (tinCheckDetails && !tinCheckSentRecently(tinCheckDetails.tinMatched, tinCheckDetails.lastChecked)))
          && <button type="button" className="btn text-ellipsis check-tin-button" onClick={ () => onCheckTin(formId) }>Check</button>}
      </div>
    </div>
  </div>);
};
TinCheckStatus.propTypes = {
  onCheckTin: PropTypes.func.isRequired,
  formId: PropTypes.number.isRequired,
  tinMatchResult: PropTypes.arrayOf(PropTypes.shape()),
  workflowStage: PropTypes.number,
  hasValidation: PropTypes.bool,
  isIndividualValidation: PropTypes.bool,
  isImyChildResult: PropTypes.bool,
  tinMatchFormProps: PropTypes.shape(),
  isFormDirty: PropTypes.bool
};
TinCheckStatus.defaultProps = {
  tinMatchResult: null,
  hasValidation: true,
  isIndividualValidation: false,
  isImyChildResult: false,
  workflowStage: 0,
  tinMatchFormProps: null,
  isFormDirty: false
};

export default TinCheckStatus;
